import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import { useAuth } from "@/hooks/useAuth"
import {ArrowLeft, Send, Loader2, Paperclip, Clock, User, X} from 'lucide-react'

const statusLabels: Record<string, string> = {
  nouveau: "Nouveau",
  en_attente: "En attente",
  en_cours: "En cours",
  attente_client: "Attente client",
  resolu: "Resolu",
  ferme: "Ferme",
  escalade: "Escalade",
  planifie: "Planifie",
  annule: "Annule",
}

const statusColor: Record<string, string> = {
  nouveau: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
  en_attente: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  en_cours: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  attente_client: "text-orange-400 bg-orange-500/10 border-orange-500/20",
  resolu: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  ferme: "text-slate-400 bg-slate-500/10 border-slate-500/20",
  escalade: "text-red-400 bg-red-500/10 border-red-500/20",
  planifie: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  annule: "text-slate-500 bg-slate-600/10 border-slate-600/20",
}

const priorityColor: Record<string, string> = {
  faible: "text-slate-400",
  normale: "text-blue-400",
  haute: "text-amber-400",
  urgente: "text-red-400",
}

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [ticket, setTicket] = useState<any>(null)
  const [messages, setMessages] = useState<any[]>([])
  const [newMessage, setNewMessage] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (id) loadData()
  }, [id])

  const loadData = async () => {
    try {
      const [ticketRes, messagesRes] = await Promise.all([
        lumi.entities.tickets.list({ filter: { _id: id }, limit: 1 }),
        lumi.entities.ticket_messages.list({
          filter: { ticketId: id },
          sort: { createdAt: 1 },
          limit: 100,
        }),
      ])
      if (ticketRes.list.length > 0) setTicket(ticketRes.list[0])
      setMessages(messagesRes.list.filter((m: any) => !m.isInternal))
    } catch (err) {
      console.error("Erreur chargement:", err)
    } finally {
      setLoading(false)
    }
  }

  const sendMessage = async () => {
    if (!newMessage.trim() || !id) return
    setSending(true)
    try {
      let attachmentUrls: string[] = []
      if (files.length > 0) {
        const results = await lumi.tools.file.upload(files)
        attachmentUrls = results.filter((r: any) => r.fileUrl).map((r: any) => r.fileUrl)
      }

      const now = new Date().toISOString()
      await lumi.entities.ticket_messages.create({
        ticketId: id,
        message: newMessage,
        authorName: user?.userName || "Client",
        authorRole: "client",
        attachments: attachmentUrls,
        isInternal: false,
        creator: user?.userId || "",
        createdAt: now,
        updatedAt: now,
      })
      setNewMessage("")
      setFiles([])
      await loadData()
    } catch (err) {
      console.error("Erreur envoi message:", err)
    } finally {
      setSending(false)
    }
  }

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles((prev) => [...prev, ...Array.from(e.target.files!)].slice(0, 5))
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="min-h-screen pt-24 lg:pt-32 pb-20">
        <div className="section-padding text-center">
          <h1 className="text-xl font-bold text-white mb-4">Ticket introuvable</h1>
          <Link to="/espace-client" className="btn-primary text-sm">Retour</Link>
        </div>
      </div>
    )
  }

  const isClosed = ["resolu", "ferme", "annule"].includes(ticket.status)

  return (
    <div className="min-h-screen pt-24 lg:pt-32 pb-20">
      <div className="section-padding max-w-4xl mx-auto">
        <Link to="/espace-client" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          Retour aux tickets
        </Link>

        {/* Ticket header */}
        <div className="card !p-6 mb-6 animate-fade-in-up">
          <div className="flex flex-wrap items-start gap-3 mb-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColor[ticket.status] || ""}`}>
              {statusLabels[ticket.status] || ticket.status}
            </span>
            <span className={`text-xs font-medium ${priorityColor[ticket.priority] || ""}`}>
              Priorite : {ticket.priority}
            </span>
            <span className="text-xs text-slate-500">
              {ticket.category?.replace(/_/g, " ")}
            </span>
          </div>
          <h1 className="text-xl lg:text-2xl font-bold text-white mb-2">{ticket.subject}</h1>
          <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-wrap">{ticket.description}</p>

          {ticket.attachments?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {ticket.attachments.map((url: string, i: number) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 text-xs text-cyan-400 hover:bg-slate-700 transition-colors"
                >
                  <Paperclip className="w-3 h-3" />
                  Piece jointe {i + 1}
                </a>
              ))}
            </div>
          )}

          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-800/50 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {new Date(ticket.createdAt).toLocaleString("fr-FR")}
            </span>
            {ticket.assignedTo && (
              <span className="flex items-center gap-1">
                <User className="w-3.5 h-3.5" />
                Assigne a : {ticket.assignedTo}
              </span>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="space-y-4 mb-6">
          {messages.map((msg) => (
            <div
              key={msg._id}
              className={`card !p-5 animate-fade-in-up ${
                msg.authorRole === "client"
                  ? "border-l-2 border-l-cyan-500/50"
                  : "border-l-2 border-l-emerald-500/50"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  msg.authorRole === "client" ? "bg-cyan-500/20 text-cyan-400" : "bg-emerald-500/20 text-emerald-400"
                }`}>
                  {msg.authorName?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <span className="text-sm font-medium text-white">{msg.authorName}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  msg.authorRole === "client" ? "bg-cyan-500/10 text-cyan-400" : "bg-emerald-500/10 text-emerald-400"
                }`}>
                  {msg.authorRole === "client" ? "Client" : "Support"}
                </span>
                <span className="text-xs text-slate-500 ml-auto">
                  {new Date(msg.createdAt).toLocaleString("fr-FR")}
                </span>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{msg.message}</p>
              {msg.attachments?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {msg.attachments.map((url: string, i: number) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-xs text-cyan-400 hover:bg-slate-700"
                    >
                      <Paperclip className="w-3 h-3" />
                      Fichier {i + 1}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Reply form */}
        {!isClosed ? (
          <div className="card !p-5 animate-fade-in-up">
            <h3 className="text-sm font-semibold text-white mb-3">Repondre</h3>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Ecrivez votre message..."
              className="input-field min-h-[100px] resize-y mb-3"
            />
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <input type="file" multiple onChange={handleFiles} className="hidden" id="msg-files" />
                <label htmlFor="msg-files" className="cursor-pointer p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-cyan-400 transition-colors">
                  <Paperclip className="w-4 h-4" />
                </label>
                {files.length > 0 && (
                  <span className="text-xs text-slate-500">{files.length} fichier(s)</span>
                )}
                {files.map((f, i) => (
                  <button key={i} onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-xs text-slate-500 hover:text-red-400 flex items-center gap-1">
                    {f.name.substring(0, 15)}
                    <X className="w-3 h-3" />
                  </button>
                ))}
              </div>
              <button
                onClick={sendMessage}
                disabled={sending || !newMessage.trim()}
                className="btn-primary text-sm !py-2.5 disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer
              </button>
            </div>
          </div>
        ) : (
          <div className="card !p-5 text-center">
            <p className="text-sm text-slate-400">Ce ticket est {statusLabels[ticket.status]?.toLowerCase()}. Vous ne pouvez plus y repondre.</p>
          </div>
        )}
      </div>
    </div>
  )
}
